import type { DocumentDirection } from "@/contracts";
import type { AuthUser } from "@/core/auth/session";
import { hasPermission } from "./permissions";

export const DOCUMENT_PERMISSIONS = {
  books: {
    read: "documents.books.read",
    create: "documents.books.create",
    update: "documents.books.update",
    delete: "documents.books.delete"
  },
  types: {
    read: "documents.types.read",
    create: "documents.types.create",
    update: "documents.types.update",
    delete: "documents.types.delete"
  },
  issuingAuthorities: {
    read: "documents.issuing_authorities.read",
    create: "documents.issuing_authorities.create",
    update: "documents.issuing_authorities.update",
    delete: "documents.issuing_authorities.delete"
  },
  incoming: {
    read: "documents.incoming.read",
    create: "documents.incoming.create",
    update: "documents.incoming.update",
    delete: "documents.incoming.delete",
    approve: "documents.incoming.approve",
    routeExecutive: "documents.incoming.route.executive",
    routeDepartment: "documents.incoming.route.department",
    process: "documents.incoming.process",
    archive: "documents.incoming.archive"
  },
  outgoing: {
    read: "documents.outgoing.read",
    create: "documents.outgoing.create",
    update: "documents.outgoing.update",
    delete: "documents.outgoing.delete",
    approve: "documents.outgoing.approve",
    process: "documents.outgoing.process",
    dispatch: "documents.outgoing.dispatch"
  },
  internal: {
    read: "documents.internal.read",
    create: "documents.internal.create",
    update: "documents.internal.update",
    delete: "documents.internal.delete",
    approve: "documents.internal.approve"
  }
} as const;

export type DocumentAction =
  | "read"
  | "create"
  | "update"
  | "delete"
  | "approve";

const permissionByDirection = {
  INCOMING: DOCUMENT_PERMISSIONS.incoming,
  OUTGOING: DOCUMENT_PERMISSIONS.outgoing
} as const;

const DOCUMENT_DIRECTIONS: DocumentDirection[] = ["INCOMING", "OUTGOING"];

export function hasDocumentPermission(
  user: Pick<AuthUser, "permissions" | "role"> | null | undefined,
  direction: DocumentDirection,
  action: DocumentAction
) {
  return hasPermission(user, permissionByDirection[direction][action]);
}

export function getAllowedDocumentDirections(
  user: Pick<AuthUser, "permissions" | "role"> | null | undefined,
  action: DocumentAction
) {
  return DOCUMENT_DIRECTIONS.filter((direction) => {
    if (action === "read" && direction === "OUTGOING") {
      return (
        hasDocumentPermission(user, direction, action) ||
        hasPermission(user, DOCUMENT_PERMISSIONS.outgoing.process) ||
        hasPermission(user, DOCUMENT_PERMISSIONS.outgoing.dispatch)
      );
    }
    return hasDocumentPermission(user, direction, action);
  });
}

export function hasInternalDocumentPermission(
  user: Pick<AuthUser, "permissions" | "role"> | null | undefined,
  action: DocumentAction
) {
  return hasPermission(user, DOCUMENT_PERMISSIONS.internal[action]);
}
